import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentCard = ({ document, onUpdateStatus, isIrc = false }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return 'CheckCircle2';
      case 'submitted':
        return 'Send';
      case 'validated':
        return 'CheckCircle';
      case 'generated':
        return 'FileCheck';
      case 'draft':
        return 'FileText';
      case 'rejected': case'failed':
        return 'XCircle';
      default:
        return 'File';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'var(--color-success)';
      case 'submitted':
        return 'var(--color-info)';
      case 'validated':
        return 'var(--color-primary)';
      case 'generated':
        return 'var(--color-primary)';
      case 'draft':
        return 'var(--color-muted-foreground)';
      case 'rejected': case'failed':
        return 'var(--color-error)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'K0.00';
    return `K${amount?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-PG', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      swt_monthly: 'SWT Monthly',
      soe_monthly: 'SOE Monthly',
      able_payroll: 'ABLE Payroll',
      nasfund_monthly: 'NASFUND Monthly',
      annual_reconciliation: 'Annual Reconciliation',
      irc_tax: 'IRC Tax'
    };
    return labels?.[type] || type;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 transition-smooth hover:shadow-md">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1">
          <Icon
            name={getStatusIcon(document?.documentStatus)}
            size={20}
            color={getStatusColor(document?.documentStatus)}
          />
          <div className="flex-1 min-w-0">
            <h4 className="font-body font-semibold text-sm text-foreground mb-1">
              {getDocumentTypeLabel(document?.documentType)}
            </h4>
            <p className="text-xs text-muted-foreground mb-2">
              Period: {document?.documentPeriod}
            </p>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="data-text font-medium text-foreground">
                {document?.totalEmployees} employees
              </span>
              {isIrc ? (
                <>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-muted-foreground">
                    SWT: {formatCurrency(document?.totalSwtWithheld)}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-muted-foreground">
                    Gross: {formatCurrency(document?.totalGrossWages)}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1 rounded-full" style={{ backgroundColor: `${getStatusColor(document?.documentStatus)}15` }}>
          <span className="text-xs font-medium capitalize" style={{ color: getStatusColor(document?.documentStatus) }}>
            {document?.documentStatus?.replace(/_/g, ' ')}
          </span>
        </div>
      </div>

      {(isIrc ? document?.ircReferenceNumber : document?.ableReferenceNumber) && (
        <div className="bg-muted/50 rounded-md p-2 mb-3">
          <div className="flex items-center gap-2">
            <Icon name="Hash" size={14} color="var(--color-muted-foreground)" />
            <span className="text-xs caption text-muted-foreground">Reference:</span>
            <span className="text-xs data-text font-medium text-foreground">
              {isIrc ? document?.ircReferenceNumber : document?.ableReferenceNumber}
            </span>
          </div>
        </div>
      )}

      <div className="space-y-1 mb-3">
        {document?.generatedAt && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Icon name="FileCheck" size={14} color="currentColor" />
            <span>Generated: {formatDate(document?.generatedAt)}</span>
          </div>
        )}
        {document?.validatedAt && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Icon name="CheckCircle" size={14} color="currentColor" />
            <span>Validated: {formatDate(document?.validatedAt)}</span>
          </div>
        )}
        {document?.submittedAt && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Icon name="Send" size={14} color="currentColor" />
            <span>Submitted: {formatDate(document?.submittedAt)}</span>
          </div>
        )}
        {document?.confirmedAt && (
          <div className="flex items-center gap-2 text-xs text-success">
            <Icon name="CheckCircle2" size={14} color="currentColor" />
            <span>Confirmed: {formatDate(document?.confirmedAt)}</span>
          </div>
        )}
      </div>

      {document?.validationErrors && (
        <div className="bg-error/10 border border-error/20 rounded-md p-2 mb-3">
          <div className="flex items-start gap-2">
            <Icon name="AlertTriangle" size={14} color="var(--color-error)" />
            <p className="text-xs text-error flex-1">Validation errors detected</p>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        {document?.documentStatus === 'draft' && (
          <Button
            variant="primary"
            size="sm"
            iconName="Play"
            iconPosition="left"
            onClick={() => onUpdateStatus?.('generated')}
          >
            Generate
          </Button>
        )}
        {document?.documentStatus === 'generated' && (
          <Button
            variant="primary"
            size="sm"
            iconName="CheckCircle"
            iconPosition="left"
            onClick={() => onUpdateStatus?.('validated')}
          >
            Validate
          </Button>
        )}
        {document?.documentStatus === 'validated' && (
          <Button
            variant="primary"
            size="sm"
            iconName="Send"
            iconPosition="left"
            onClick={() => onUpdateStatus?.('submitted')}
          >
            Submit to Portal
          </Button>
        )}
        {(document?.documentStatus === 'failed' || document?.documentStatus === 'rejected') && (
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={() => onUpdateStatus?.('draft')}
          >
            Retry
          </Button>
        )}
        {document?.documentUrl && (
          <Button
            variant="ghost"
            size="sm"
            iconName="Download"
            iconPosition="left"
          >
            Download
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          iconName="Eye"
          iconPosition="left"
        >
          View Details
        </Button>
      </div>
    </div>
  );
};

export default DocumentCard;